import { ProductImageModel } from './ProductImageModel'
import { ProductStoreModel } from './ProductStoreModel'
import { sum, cloneDeep, orderBy, uniqBy } from 'lodash'
import { IntegrationSourceEnum, getIntegrationSourceEnumDescription, AttributeTypeEnum } from '@c2b/muven-commons'
import { ProductCategoryChannelModel } from './ProductCategoryChannelModel'
import { BrandModel } from '../../brand/models/BrandModel'
import { ProductAttributeModel } from './ProductAttributeModel'
import { ProductBidimensionalGridModel } from './ProductBidimensionalGridModel'
import { SubscriberBackendMonitor } from '../../subscriber/models/SubscriberModel'
import { AllowImportEnum } from '@c2b/muven-commons';
import { ProductStoreChannelModel } from './ProductStoreChannelModel'
import { StoreModel } from '../../store/models/StoreModel'

export class ProductModel {
    static normalizeV1(product: ProductModel, subscriber: SubscriberBackendMonitor): ProductModel {
        const p = cloneDeep(product);

        if (!subscriber.isBackendBimer()) {
            return p
        }

        p.description = p.description || p.children?.[0]?.description || ""
        p.descriptionShort = p.descriptionShort || p.children?.[0]?.descriptionShort || ""
        p.descriptionLong = p.descriptionLong || p.children?.[0]?.descriptionLong || ""
        p.seoTitle = p.seoTitle || p.children?.[0]?.seoTitle || ""
        p.seoDescription = p.seoDescription || p.children?.[0]?.seoDescription || ""
        p.seoKeywords = p.seoKeywords || p.children?.[0]?.seoKeywords || ""
        p.height = p.height || p.children?.[0]?.height || 0
        p.width = p.width || p.children?.[0]?.width || 0
        p.depth = p.depth || p.children?.[0]?.depth || 0
        p.weight = p.weight || p.children?.[0]?.weight || 0
        p.costPrice = p.children?.[0]?.stores?.[0]?.costPrice || 0
        p.listPrice = p.children?.[0]?.stores?.[0]?.listPrice || 0
        p.sellPrice = p.children?.[0]?.stores?.[0]?.sellPrice || 0
        p.videoUrl = p.videoUrl || p.children?.[0]?.videoUrl;
        // DESATIVADO em 07/10/2026 (ver ProductIntegrationDiagnosticModel, "pai de grade sem
        // product_store"): marcava também todo produto simples Bimer, cujo pai nunca tem
        // stores, e o diagnóstico passou a acusar "sem vínculo com empresa" em quase tudo.
        // Para religar, marcar só no ramo hasGrid, logo abaixo.
        // p.storesCopiedFromChild = !(p.stores || []).length && !!(p.children?.[0]?.stores || []).length
        p.stores = cloneDeep(p.children?.[0]?.stores) || [];

        const hasGrid = (p.children || []).find((ch) => {
            return ch.hasAttributeTypeGrid()
        })

        if (hasGrid) {
            p.children.forEach((ch) => {
                ch.costPrice = ch.stores?.[0]?.costPrice || 0
                ch.listPrice = ch.stores?.[0]?.listPrice || 0
                ch.sellPrice = ch.stores?.[0]?.sellPrice || 0
            })
        } else {
            p.allowImport = p.children?.[0]?.allowImport
            p.skuCode = p.skuCode || p.children?.[0]?.skuCode || ""
            p.eanCode = p.eanCode || p.children?.[0]?.eanCode || ""
            p.ncmCode = p.ncmCode || p.children?.[0]?.ncmCode || ""
            p.updatedAt = p.children?.[0]?.updatedAt
            p.weight = p.children?.[0]?.weight
            p.height = p.children?.[0]?.height
            p.width = p.children?.[0]?.width
            p.depth = p.children?.[0]?.depth
            p.firstChildId = p.children?.[0]?.id
            p.images = cloneDeep(p.children?.[0]?.images) || [];
            p.children = [];
        }

        return p;
    }

    id?: string | number = undefined
    firstChildId?: string | number // only exists when it's a simple product from Alterdata's Backend Bimer
    idSource?: string | number = undefined
    codeSource?: string = undefined
    idSourceSku?: string | number = undefined
    createdAt: Date | string | undefined = undefined
    updatedAt: Date | string | undefined = undefined
    seoTitle: string = ''
    name: string = ''
    description: string = ''
    descriptionLong: string = ''
    descriptionShort: string = ''
    seoDescription: string = ''
    additionalDeliveryDays: number = 0
    videoUrl: string = ''
    eanCode: string = ''
    ncmCode: string = ''
    isbnCode: string = ''
    isGridVisible: boolean = false
    isActive: boolean = true
    listPrice: number = 0
    sellPrice: number = 0
    costPrice: number = 0
    showWithoutStock: boolean = false
    seoKeywords: string = ''
    skuCode: string = ''
    height?: number
    depth?: number
    width?: number
    weight?: number
    isSku: boolean = false
    isVirtual: boolean = false
    modificationSource: IntegrationSourceEnum;
    parent?: ProductModel = undefined
    brand?: BrandModel = undefined
    allowImport: AllowImportEnum = AllowImportEnum.All
    children: ProductModel[] = []
    images: ProductImageModel[] = []
    stores: ProductStoreModel[] = []
    /**
     * normalizeV1 copia as stores da primeira variação para o pai. Isto indica
     * que o pai não tinha product_store próprio antes dessa cópia, o que o
     * diagnóstico de integração precisa saber (na Tray o pai não sobe assim).
     */
    storesCopiedFromChild: boolean = false
    categories: ProductCategoryChannelModel[] = []
    attributes: ProductAttributeModel[] = []
    stockConversionRatio: number = 0
    loadingListAction: boolean = false

    constructor(p?: Partial<ProductModel>) {
        Object.assign(this, p)

        this._parseCreatedAt()
        this._parseUpdatedAt()
        this._parseBrand()
        this._parseProductStores()
        this._parseProductImages()
        this._parseProductCategories()
        this._parseAttributes()
        this._parseChildren()
        this._normalizePrices()

        if (!!this.parent) {
            this._parseParent()
        }
    }

    hasParent(): boolean {
        return !!this.parent;
    }

    hasStockBlocked(): boolean {
        if (!this.hasChildren()) {
            for (const store of this.stores) {
                if (store.blockStock) {
                    return true
                }
            }
        } else {
            for (const child of this.children) {
                for (const store of child.stores) {
                    if (store.blockStock) {
                        return true
                    }
                }
            }
        }

        return false
    }

    getVariationsStringified(): string {
        let variationInfo = ""
        const grid = this.getAttributeTypeGridList()

        if (!grid || !grid.length) {
            return ""
        }

        for (const item of grid) {
            variationInfo += `${item.attribute!.name} ${item.value}, `
        }

        return variationInfo.replace(/, $/, '')
    }

    getStoreChannelsWithIdExternalChannel(): ProductStoreChannelModel[] {
        const productStoreChannels: ProductStoreChannelModel[] = []

        this.stores.forEach((productStore) => {
            productStoreChannels.push(...productStore.channels)
        })

        return uniqBy(productStoreChannels.filter((psc) => !!psc.idExternalChannel), 'channel.id')
    }

    getUniqueStoreChannels(variations: any[] | null = null): ProductStoreChannelModel[] {

        const productStoreChannels: ProductStoreChannelModel[] = [];
        let stores = this.stores;

        if (variations && variations.length > 0) {
            const variationsStores = variations.reduce((acc, variation) => {
                if (variation.stores) {
                    return acc.concat(variation.stores);
                }
                return acc;
            }, [] as ProductStoreChannelModel[]);

            // substitui stores pelas stores das variações
            stores = variationsStores;
        }

        stores.forEach((productStore) => {
            
            const filteredChannels = productStore.channels.filter(channel =>
                channel.channel?.idPriceManagementStore === undefined ||
                Number(channel.channel?.idPriceManagementStore) === Number(productStore.idStore)
            );

            filteredChannels.forEach(ch => {
                const copy = new ProductStoreChannelModel(ch);
                const store = productStore.store;
                if (store) {
                    let storeName = `${store.idSource} - ${store.name}`;
                    if (!store.isActive) { storeName += ' (INATIVO)'; }
                    copy.storeName = storeName;
                }
                productStoreChannels.push(copy);
            });
        });

        const uniqueChannels = [];
        const channelIds = new Set();

        for (const channel of productStoreChannels) {
            const idCombo = `${channel.channel?.id}-${channel.idExternalChannel}`;
            if (idCombo && !channelIds.has(idCombo)) {
                uniqueChannels.push(channel);
                channelIds.add(idCombo);
            }
        }
        return uniqueChannels;
    }

    getAllowImportDescription(): string {
        switch (this.allowImport) {
            case AllowImportEnum.All:
                return "Importa TUDO da retaguarda";

            case AllowImportEnum.AllExceptImages:
                return "Importa TUDO da retaguarda EXCETO imagens";

            case AllowImportEnum.StockAndPrice:
                return "Importa APENAS ESTOQUE e PREÇO da retaguarda";

            case AllowImportEnum.Stock:
                return "Importa APENAS ESTOQUE da retaguarda";

            default:
                return "";
        }
    }

    getImages(): ProductImageModel[] {
        return orderBy(this.images || [], ['id'], ['asc']);
    }

    hasVideoUrl(): boolean {
        return !!this.videoUrl
    }

    hasSkuCode(): boolean {
        return !!this.skuCode;
    }

    setSkuCode(sku: string): void {
        this.skuCode = sku;
    }

    toBidimensionalGrid(): ProductBidimensionalGridModel {
        return new ProductBidimensionalGridModel().fromProduct(this);
    }

    isNew(): boolean {
        return !this.id;
    }

    hasChildren(): boolean {
        return !!this.children.length;
    }

    hasMoreThanOneChildren(): boolean {
        return this.children.length > 1;
    }

    /** Quantos itens a grade tem. Já considera a normalização do normalizeV1. */
    getVariationsCount(): number {
        return this.children.length;
    }

    hasDescription(): boolean {
        return !!this.description
    }

    hasDescriptionShort(): boolean {
        return !!this.descriptionShort
    }

    hasDescriptionLong(): boolean {
        return !!this.descriptionLong
    }

    getDescriptionWithLineBreaks(): string {
        return this._replaceLineBreakWithBr(this.description)
    }

    getDescriptionShortWithLineBreaks(): string {
        return this._replaceLineBreakWithBr(this.descriptionShort)
    }

    getDescriptionLongWithLineBreaks(): string {
        return this._replaceLineBreakWithBr(this.descriptionLong)
    }

    getBrand(): BrandModel {
        if (this.brand?.name) {
            return this.brand
        }

        if (this.parent) {
            if (this.parent?.brand?.name) {
                return this.parent.brand
            }
        }

        return new BrandModel()
    }

    getIdSource(): string | number {
        if (this.isSku) {
            return this.idSourceSku || ""
        }

        return this.idSource || ""
    }

    getMainImage(): ProductImageModel {
        let mainImage = this.images.find((img) => {
            return img.isMain && img.isActive
        })

        if (mainImage) {
            return mainImage
        }

        mainImage = this.images.find((img) => {
            return img.isActive
        }) || new ProductImageModel()

        mainImage.isMain = true

        return mainImage
    }

    getCategories(): ProductCategoryChannelModel[] {
        if (!!this.parent) {
            return uniqBy(this.parent.categories, 'category.id')
        }

        return uniqBy(this.categories, 'category.id')
    }

    hasCodeSource(): boolean {
        return !!this.codeSource;
    }

    hasCategories(): boolean {
        return !!this.getCategories().length
    }

    hasAnyImage(): boolean {
        const hasAnyImage = this.hasImages()

        if (hasAnyImage) {
            return true
        }

        if (this.hasChildren()) {
            for (const child of this.children) {
                if (child.hasImages()) {
                    return true
                }
            }
        }

        return false
    }

    hasImages(): boolean {
        return !!this.images?.length;
    }

    hasMainImage(): boolean {
        return !!this.images.find((img) => {
            return img.isMain && img.isActive
        })
    }

    hasStores(): boolean {
        return !!this.stores.length
    }

    hasMoreThanOneStore(): boolean {
        return !!this.stores && this.stores.length > 1
    }

    hasAttributes(): boolean {
        return !!this.attributes.length
    }

    hasAttributeTypeGrid(): boolean {
        if (!this.attributes.length) {
            return false
        }

        return !!this.attributes.find((attr) => {
            return attr.attribute?.type === AttributeTypeEnum.Grid
        })
    }

    getAttributeTypeGridList(): ProductAttributeModel[] {
        if (!this.attributes.length) {
            return []
        }

        return orderBy(this.attributes.filter((prodAttr) => {
            return prodAttr.attribute!.isGrid()
        }), ['attribute.name'], ['asc'])
    }

    totalStockQtyInStores(): number {
        return sum(this.stores.map((s: ProductStoreModel) => {
            if (!s.stockQty) {
                return 0
            }

            return s.stockQty
        }))
    }

    totalReservedStockQtyInStores(): number {
        const result = sum(this.stores.map((s: ProductStoreModel) => {
            if (!s.reservedStockQty || s.reservedStockQty < 0) {
                return 0
            }

            return s.reservedStockQty
        }))

        return result >= 0 ? result : 0
    }

    totalAvailableStockQtyInStores(): number {
        return this.totalStockQtyInStores() - this.totalReservedStockQtyInStores()
    }

    getIntegrationSourceEnumDescription(): string {
        return getIntegrationSourceEnumDescription(this.modificationSource)
    }

    getStoreList(): StoreModel[] {
        return this.stores.map((productStore) => {
            return productStore.store as StoreModel;
        })
    }

    getActiveStoreList(): StoreModel[] {
        return this.stores.map((productStore) => {
            return productStore.store as StoreModel;
        }).filter((store) => {
            return !!store && store.isActive
        });
    }

    getProductStoreInfo(store: StoreModel): ProductStoreModel {
        // @ts-ignore
        return this.stores.find((productStore) => String(productStore.idStore) === String(store.id)) as ProductStoreModel
    }

    private _parseBrand(): void {
        this.brand = new BrandModel(this.brand)
    }

    private _parseCreatedAt(): void {
        if (typeof this.createdAt === 'string') {
            this.createdAt = new Date(this.createdAt)
        }
    }

    private _parseUpdatedAt(): void {
        if (typeof this.updatedAt === 'string') {
            this.updatedAt = new Date(this.updatedAt)
        }
    }

    private _parseProductImages(): void {
        this.images = (this.images || []).map((img) => new ProductImageModel(img))
    }

    private _parseProductStores(): void {
        this.stores = orderBy(this.stores.map((s) => new ProductStoreModel(s)), ['store.name'], ['asc'])
    }

    private _parseProductCategories(): void {
        this.categories = this.categories.map((pc) => {
            return new ProductCategoryChannelModel(pc)
        })
    }

    private _parseParent(): void {
        this.parent = new ProductModel(this.parent)
    }

    private _parseAttributes(): void {
        this.attributes = this.attributes.map((prodAttr) => {
            return new ProductAttributeModel(prodAttr);
        })
    }

    private _parseChildren(): void {
        this.children = this.children.map((p) => {
            return new ProductModel(p);
        })
    }

    private _normalizePrices(): void {
        if (this.stores.length === 1) {
            this.costPrice = this.stores?.[0]?.costPrice || 0
            this.listPrice = this.stores?.[0]?.listPrice || 0
            this.sellPrice = this.stores?.[0]?.sellPrice || 0

            return
        }

        if (this.stores.length > 1) {
            const productStoreWithPrice = this.stores.find((ps) => {
                return ps.sellPrice > 0.01
            })

            if (!productStoreWithPrice) {
                return
            }

            this.costPrice = productStoreWithPrice.costPrice || 0
            this.listPrice = productStoreWithPrice.listPrice || 0
            this.sellPrice = productStoreWithPrice.sellPrice || 0

            return
        }
    }

    private _replaceLineBreakWithBr(str: string): string {
        if (!str) {
            return ""
        }

        return str.replace(/\r\n|\n|\r/g, '<br/>')
    }
}
